import React from "react";
import Button from "@ui/Button";
import useModal from "@ui/lib/hooks/useModal";
import Modal from "@ui/ModalV2";
import Checkbox from "@ui/Checkbox";
import Text from "@ui/Text";
import SearchInputV2 from "@ui/Input/SearchInputV2";
import { css } from "@linaria/core";
import { colors, spacings } from "@ui/Theme";
import { useDelete, useGet } from "../../../api/useCall";
import { userMessage } from "../../../api/error";
import { FlaskFill } from "@ui/IconV2/react";
import { color } from "@ui";
import t from "../../../i18n";

interface Props {
  canEdit: boolean;
}

type RollbackStatus = "idle" | "waiting" | "deleting" | "success" | "failed";
type ProjectRollbackState = { status: RollbackStatus; error: string | null };

type RollbackProject = {
  project: {
    id: string;
    name: string;
    isDraft: boolean;
  };
  manualFaultEventIds: string[];
};

const MAX_CONCURRENT_ROLLBACKS = 4;

const StyledLab = ({ className }: { className?: string }) => <FlaskFill className={className} />;

const ManualFaultMigrationRollback = ({ canEdit }: Props) => {
  const { modal: modalIsOpen, openModal, closeModal } = useModal();

  const getRollbackProjects = useGet("/projects/manualFaultEvents", false);
  const deleteManualFaultEvent = useDelete(
    "/projects/:projectId(uuid)/manualFaultEvent/:manualFaultEventId(uuid)",
    false,
  );

  const [projects, setProjects] = React.useState<RollbackProject[]>([]);
  const [isLoading, setIsLoading] = React.useState(false);
  const [loadError, setLoadError] = React.useState<string | null>(null);

  const [search, setSearch] = React.useState("");
  const [selectedProjectIds, setSelectedProjectIds] = React.useState<Set<string>>(() => new Set());
  const [rollbackStates, setRollbackStates] = React.useState<Record<string, ProjectRollbackState>>(
    {},
  );
  const [isRunning, setIsRunning] = React.useState(false);

  const loadProjects = React.useCallback(async () => {
    setIsLoading(true);
    setLoadError(null);
    try {
      const response = await getRollbackProjects({});
      setProjects(response.body);
    } catch (error) {
      const hasBody = error instanceof Error && Boolean(error.message);
      const content = hasBody ? error.message : userMessage(error);
      setLoadError(content);
      setProjects([]);
    } finally {
      setIsLoading(false);
    }
  }, [getRollbackProjects]);

  React.useEffect(() => {
    if (modalIsOpen) void loadProjects();
  }, [loadProjects, modalIsOpen]);

  const selectedCount = selectedProjectIds.size;
  const allSelected = selectedCount > 0 && selectedCount === projects.length;
  const isPartialSelected = selectedCount > 0 && selectedCount < projects.length;

  const filteredProjects = React.useMemo(() => {
    const q = search.trim().toLocaleLowerCase();
    if (!q) return projects;
    return projects.filter(project => project.project.name.toLocaleLowerCase().includes(q));
  }, [projects, search]);

  const handleClose = React.useCallback(() => {
    if (isRunning) return;
    closeModal();
    setSearch("");
    setSelectedProjectIds(new Set());
    setRollbackStates({});
  }, [closeModal, isRunning]);

  const toggleProject = React.useCallback((projectId: string, nextChecked: boolean) => {
    setSelectedProjectIds(prev => {
      const next = new Set(prev);
      if (nextChecked) next.add(projectId);
      else next.delete(projectId);
      return next;
    });
    setRollbackStates(prev => {
      const current = prev[projectId];
      if (!current) return prev;
      delete prev[projectId];
      return prev;
    });
  }, []);

  const toggleSelectAll = React.useCallback(
    (nextChecked: boolean) => {
      setSelectedProjectIds(() => {
        if (!nextChecked) return new Set();
        return new Set(projects.map(project => project.project.id));
      });
      setRollbackStates({});
    },
    [projects],
  );

  const getProjectState = (projectId: string): ProjectRollbackState =>
    rollbackStates[projectId] ?? { status: "idle", error: null };

  const globalStatus = React.useMemo(() => {
    const selectedIds = [...selectedProjectIds];
    if (!selectedIds.length) return null;

    let waiting = 0;
    let deleting = 0;
    let success = 0;
    let failed = 0;

    for (const id of selectedIds) {
      const state = rollbackStates[id]?.status ?? "idle";
      if (state === "waiting") waiting += 1;
      if (state === "deleting") deleting += 1;
      if (state === "success") success += 1;
      if (state === "failed") failed += 1;
    }

    return t("customization.section.failure_analysis.migration.rollback.global_status", {
      deleting,
      failed,
      success,
      waiting,
    });
  }, [rollbackStates, selectedProjectIds]);

  const rollbackSelected = React.useCallback(async () => {
    if (isRunning) return;
    if (selectedProjectIds.size === 0) return;

    setIsRunning(true);

    const selected = projects.filter(project => selectedProjectIds.has(project.project.id));
    setRollbackStates(prev => {
      const next: Record<string, ProjectRollbackState> = { ...prev };
      for (const project of selected) next[project.project.id] = { status: "waiting", error: null };
      return next;
    });

    let cursor = 0;
    let inFlight = 0;
    let done = 0;

    const getErrorContent = (error: unknown) => {
      const hasBody = error instanceof Error && Boolean(error.message);
      return hasBody ? error.message : userMessage(error);
    };

    await new Promise<void>(resolve => {
      const launchNext = () => {
        if (done === selected.length) {
          resolve();
          return;
        }

        while (inFlight < MAX_CONCURRENT_ROLLBACKS && cursor < selected.length) {
          const project = selected[cursor]!;
          cursor += 1;
          inFlight += 1;

          setRollbackStates(prev => ({
            ...prev,
            [project.project.id]: { status: "deleting", error: null },
          }));

          const removeProjectEvents = async () => {
            for (const manualFaultEventId of project.manualFaultEventIds) {
              await deleteManualFaultEvent({
                routeParams: {
                  projectId: project.project.id,
                  manualFaultEventId,
                },
              });
            }
          };

          removeProjectEvents()
            .then(() => {
              setRollbackStates(prev => ({
                ...prev,
                [project.project.id]: { status: "success", error: null },
              }));
              setProjects(prev => prev.filter(item => item.project.id !== project.project.id));
              setSelectedProjectIds(prev => {
                const next = new Set(prev);
                next.delete(project.project.id);
                return next;
              });
            })
            .catch(error => {
              setRollbackStates(prev => ({
                ...prev,
                [project.project.id]: { status: "failed", error: getErrorContent(error) },
              }));
            })
            .finally(() => {
              inFlight -= 1;
              done += 1;
              launchNext();
            });
        }
      };

      launchNext();
    });

    setIsRunning(false);
  }, [deleteManualFaultEvent, isRunning, projects, selectedProjectIds]);

  if (!canEdit) return null;

  return (
    <>
      <Modal
        title={t("customization.section.failure_analysis.migration.rollback.modal.title")}
        isOpen={modalIsOpen}
        onRequestClose={handleClose}
        closable={!isRunning}
        size="m"
      >
        <div className={container}>
          <div className={header}>
            <Button
              variant="primary"
              onClick={rollbackSelected}
              disabled={isRunning || selectedCount === 0}
            >
              {t("customization.section.failure_analysis.migration.rollback.rollback.button", {
                count: selectedCount,
              })}
            </Button>
            <Text type="body2" className={globalStatusText}>
              {globalStatus ?? "..."}
            </Text>
          </div>

          <div className={filters}>
            <SearchInputV2
              aria-label={t(
                "customization.section.failure_analysis.migration.rollback.search.aria_label",
              )}
              placeholder={t(
                "customization.section.failure_analysis.migration.rollback.search.placeholder",
              )}
              value={search}
              onChange={e => setSearch(e.currentTarget.value)}
              disabled={isRunning || isLoading}
              slim
            />
            <Checkbox
              checked={allSelected}
              partial={isPartialSelected}
              onChange={e => toggleSelectAll(e.currentTarget.checked)}
              disabled={isRunning || projects.length === 0 || isLoading}
            >
              {t("customization.section.failure_analysis.migration.rollback.select_all", {
                total: projects.length,
              })}
            </Checkbox>
          </div>

          <div className={list} role="list">
            {isLoading ? (
              <Text type="body2" className={emptyState}>
                {t("customization.section.failure_analysis.migration.rollback.loading")}
              </Text>
            ) : loadError ? (
              <Text type="body2" className={errorText}>
                {t("customization.section.failure_analysis.migration.rollback.load_error")}
              </Text>
            ) : filteredProjects.length === 0 ? (
              <Text type="body2" className={emptyState}>
                {t("customization.section.failure_analysis.migration.rollback.empty_state")}
              </Text>
            ) : (
              filteredProjects.map(project => {
                const isSelected = selectedProjectIds.has(project.project.id);
                const state = getProjectState(project.project.id);
                const disabled = isRunning;

                return (
                  <div key={project.project.id} role="listitem" className={row}>
                    <Checkbox
                      checked={isSelected}
                      onChange={e => toggleProject(project.project.id, e.currentTarget.checked)}
                      disabled={disabled}
                    >
                      <span className={rowLabel}>
                        <span className={projectName}>
                          {project.project.name}
                          <span className={projectCount}>
                            {t(
                              "customization.section.failure_analysis.migration.rollback.project_events",
                              {
                                count: project.manualFaultEventIds.length,
                              },
                            )}
                          </span>
                        </span>
                        <span className={projectMeta}>
                          {project.project.isDraft ? (
                            <div className={iconGroup} title={t("commons.tooltip.research")}>
                              <span>
                                <StyledLab className={labIcon} />
                              </span>
                            </div>
                          ) : null}
                          <span className={projectStatus} data-status={state.status}>
                            {state.status === "idle" &&
                              (isSelected
                                ? t(
                                    "customization.section.failure_analysis.migration.rollback.project_status.selected",
                                  )
                                : "")}
                            {state.status === "waiting" &&
                              t(
                                "customization.section.failure_analysis.migration.rollback.project_status.waiting",
                              )}
                            {state.status === "deleting" &&
                              t(
                                "customization.section.failure_analysis.migration.rollback.project_status.deleting",
                              )}
                            {state.status === "success" &&
                              t(
                                "customization.section.failure_analysis.migration.rollback.project_status.success",
                              )}
                            {state.status === "failed" &&
                              t(
                                "customization.section.failure_analysis.migration.rollback.project_status.failed",
                              )}
                          </span>
                        </span>
                        {state.error && <span className={errorText}>{state.error}</span>}
                      </span>
                    </Checkbox>
                  </div>
                );
              })
            )}
          </div>

          {isRunning && (
            <Text type="body2" className={runningHint}>
              {t("customization.section.failure_analysis.migration.rollback.running_hint")}
            </Text>
          )}
        </div>
      </Modal>
      <Button variant="link" onClick={openModal} disabled={!canEdit}>
        {t("customization.section.failure_analysis.migration.rollback.open_modal_button")}
      </Button>
    </>
  );
};

export default ManualFaultMigrationRollback;

const labIcon = css`
  display: flex;
  align-items: baseline;
  color: ${color.carbon800};
  height: 14px;
`;

const iconGroup = css`
  flex-shrink: 0;
  display: flex;
  align-items: center;
`;

const container = css`
  display: flex;
  flex-direction: column;
  gap: ${spacings.m};
  padding-top: ${spacings.s};
`;

const header = css`
  display: flex;
  flex-direction: column;
  gap: ${spacings.xs};
`;

const globalStatusText = css`
  color: ${colors.carbon700};
`;

const filters = css`
  display: flex;
  flex-direction: column;
  gap: ${spacings.s};
`;

const list = css`
  border: 1px solid ${colors.titanium200};
  border-radius: 8px;
  padding: ${spacings.s};
  max-height: 420px;
  overflow: auto;
  display: flex;
  flex-direction: column;
  gap: ${spacings.xs};
`;

const row = css`
  padding: ${spacings.xxs} 0;
`;

const rowLabel = css`
  width: 100%;
  display: grid;
  grid-template-columns: 1fr auto;
  align-items: center;
  gap: ${spacings.s};
`;

const projectName = css`
  display: flex;
  flex-direction: column;
  gap: ${spacings.xxs};
  overflow: hidden;
`;

const projectCount = css`
  font-size: 12px;
  color: ${colors.carbon600};
`;

const projectMeta = css`
  display: flex;
  align-items: center;
  gap: ${spacings.s};
  flex-shrink: 0;
`;

const projectStatus = css`
  font-size: 12px;
  color: ${colors.carbon600};

  &[data-status="deleting"],
  &[data-status="waiting"] {
    color: ${colors.carbon700};
  }

  &[data-status="success"] {
    color: ${colors.success};
  }

  &[data-status="failed"] {
    color: ${colors.error};
  }
`;

const errorText = css`
  grid-column: 1 / -1;
  font-size: 12px;
  color: ${colors.error};
  margin-top: ${spacings.xxs};
  white-space: normal;
`;

const runningHint = css`
  color: ${colors.carbon700};
`;

const emptyState = css`
  color: ${colors.carbon600};
`;
